import _ from 'lodash'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

import { useEffect, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import {
  enterWaitingRoomSocketEvent,
  getWaitingRoom,
  leaveWaitingRoomSocketEvent,
  selectWaitingRoomById,
  selectWaitingRooms,
} from '@/redux/reducers/waiting-room.reducer'

import WaitingRoomCard from '../components/WaitingRoomCard'
import { Grid } from '@mui/material'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'
import { useGetWaitingRoomDoctorsQuery } from '@/core/services/waiting-room'
import { skipToken } from '@reduxjs/toolkit/query'
import { useRoomDoctorStatuses } from '@/hooks/useRoomDoctorStatuses'

dayjs.extend(isBetween)

export default function WaitingRoomCardContainer() {
  const dispatch = useAppDispatch()

  const authUser = useAppSelector(selectAuthUser)

  const waitingRooms = useAppSelector(selectWaitingRooms)

  const prioritizedWaitingRoom = useMemo(() => _.find(waitingRooms, ['isPrioritized', true]), [waitingRooms])

  const waitingRoom = useAppSelector((state) => selectWaitingRoomById(state, prioritizedWaitingRoom?._id || ''))

  const { statuses, isReady } = useRoomDoctorStatuses(waitingRoom?._id)

  const { doctors } = useGetWaitingRoomDoctorsQuery(waitingRoom?._id ?? skipToken, {
    selectFromResult: ({ data }) => ({
      doctors:
        data?.map((doctor) => ({
          id: doctor._id,
          status: statuses[doctor._id] ?? (isReady ? ('offline' as const) : undefined),
          name: `${doctor.userInfo.name} ${doctor.userInfo.surname}`,
        })) ?? [],
    }),
  })

  const isWaitingRoomOpening = useMemo(() => {
    let isOpening = false

    if (!waitingRoom) return isOpening

    const openingHour = _.find(waitingRoom.openingHours, ({ day }) => day === dayjs().day())
    if (openingHour) {
      const { periods } = openingHour
      isOpening = _.some(periods, (period) => dayjs().isBetween(dayjs(period.open, 'HH:mm'), dayjs(period.close, 'HH:mm')))
    }

    return isOpening
  }, [waitingRoom])

  const specialities = useMemo(() => waitingRoom?.specialities.map((speciality) => _.capitalize(speciality.name)) || [], [waitingRoom])

  useEffect(() => {
    if (!waitingRoom) return
    dispatch(getWaitingRoom(waitingRoom?._id))
  }, [dispatch, waitingRoom])

  useEffect(() => {
    if (!waitingRoom) return
    dispatch(enterWaitingRoomSocketEvent(waitingRoom?._id))

    return () => {
      dispatch(leaveWaitingRoomSocketEvent(waitingRoom?._id))
    }
  }, [dispatch, waitingRoom])

  if (!waitingRoom) return null
  if (!authUser) return null
  return (
    <Grid container spacing={3} sx={{ height: '100%' }}>
      {isStaff(authUser) && authUser.status === 'PDS' && prioritizedWaitingRoom && (
        <Grid item xs={12} md={8}>
          <WaitingRoomCard
            description={waitingRoom?.description}
            doctors={doctors}
            isOpening={isWaitingRoomOpening}
            name={waitingRoom.name}
            specialities={specialities}
            waitingRoomId={waitingRoom?._id}
            price={waitingRoom.price !== 0 ? waitingRoom.price : undefined}
          />
        </Grid>
      )}
    </Grid>
  )
}
